import { addToast } from "components/ToastContainer/ToastStore";
import { routeConstants } from "constants/routeConstants";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useResetPasswordMutation } from "slice/userAuth";
import { validations } from "utils/validations";

export const useResetPassword = () => {
    const navigateTo = useNavigate();

    const [inputs, setInputs] = useState({
        password: "",
        confirmPassword: "",
    });

    const [inputErrors, setInputErrors] = useState({
        password: "",
        confirmPassword: "",
    });

    const [resetPassword, { isSuccess, error, isLoading }] =
        useResetPasswordMutation();

    const params = new URLSearchParams(window.location.search);

    const token = params.get("reset_token"),
        email = params.get("email");

    const getConfirmPasswordError = useCallback(
        (val: string) => {
            let error = "";
            if (val === "") {
                error = "Please enter a valid password.";
            } else if (val !== inputs.password) {
                error =
                    "Please make sure that password and confirm password are same.";
            }

            return error;
        },
        [inputs.password]
    );

    const handleInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const { name, value } = e.target;
            if (name === "confirmPassword") {
                setInputErrors((prev) => ({
                    ...prev,
                    [name]: getConfirmPasswordError(value),
                }));
            } else {
                setInputErrors((prev) => ({
                    ...prev,
                    [name]: validations(name, value),
                }));
            }
            setInputs((prev) => ({
                ...prev,
                [name]: value,
            }));
        },
        [getConfirmPasswordError]
    );

    const checkForErrors = () => {
        let flag = true;
        const password = validations("password", inputs.password);
        const confirmPassword = getConfirmPasswordError(inputs.confirmPassword);

        if (password !== "" || confirmPassword !== "") {
            flag = false;
        }
        setInputErrors({
            password: validations("password", inputs.password),
            confirmPassword: getConfirmPasswordError(inputs.confirmPassword),
        });

        return flag;
    };

    const handleResetPassword = (e: React.FormEvent) => {
        e.preventDefault();
        if (checkForErrors()) {
            resetPassword({
                email,
                token,
                password: inputs.password,
                password_confirmation: inputs.confirmPassword,
            });
        }
    };

    useEffect(() => {
        let id: NodeJS.Timeout;
        if (isSuccess) {
            navigateTo(routeConstants.login, {
                state: {
                    toast: true,
                    data: {
                        type: "success",
                        text: "Reset password successful!",
                    },
                },
            });
        }

        return () => clearTimeout(id);
    }, [isSuccess, navigateTo]);

    useEffect(() => {
        if (error && Object.keys(error).length > 0) {
            addToast({
                type: "error",
                text: "Something went wrong.",
            });
        }
    }, [error]);

    useEffect(() => {
        if (!token || !email) {
            navigateTo(routeConstants.login);
        }
    }, [email, navigateTo, token]);

    return {
        isLoading,
        inputs,
        inputErrors,
        handleResetPassword,
        handleInputChange,
    };
};
